import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Observable, Subscription } from 'rxjs';
import { TenantSelectorComponent } from 'src/app/tenant-selector/tenant-selector.component';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IUserAccess } from 'src/app/shared/user-view/models/tenant-user-access.model';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { ConfirmDialogComponent } from 'src/app/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { UiState } from '../../state/ui.state';
import { ToggleDarkMode } from '../../state/ui.actions';
import { RoutesEnum } from '../../enums/routes.enum';
import { ClearAllSavedFilters, ClearFilterDetails } from '../../state/main-table-filter.actions';
import { TableFilterStorageService } from '../../services/table-filter-storage.service';

@Component({
  selector: 'app-logged-in-as-popup',
  templateUrl: './logged-in-as-popup.component.html',
  styleUrls: ['./logged-in-as-popup.component.scss'],
  standalone: false,
})
export class LoggedInAsPopupComponent implements OnInit, OnDestroy {
  userAccess$: Observable<IUserAccess> = this.store.select(TenantUserAccessState.userAccess);
  userCurrentTenantId$: Observable<string> = this.store.select(
    TenantUserAccessState.currentTenantId
  );
  isDarkMode: boolean;
  filterSavingEnabled: boolean;
  firstRun = true;
  profileSettingsAvailable = false;
  tenantUserEmail: string;
  tenantUserName: string;
  tenantPosition: string;
  userAccess: IUserAccess;

  get hasSavedFilters(): boolean {
    return this.tableFilterStorageService.hasSavedFilters();
  }
  private isDarkMode$: Observable<boolean> = this.store.select(UiState.isDarkMode);
  private subscription = new Subscription();

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private store: Store,
    private tableFilterStorageService: TableFilterStorageService
  ) {}

  ngOnInit(): void {
    this.filterSavingEnabled = this.tableFilterStorageService.isFilterSavingEnabled();

    this.subscription.add(
      this.isDarkMode$.subscribe((mode) => {
        if (this.firstRun) {
          this.firstRun = false;
        }
        this.isDarkMode = mode;
      })
    );

    this.subscription.add(
      this.userAccess$.subscribe((userAccess: IUserAccess) => {
        this.userAccess = userAccess;
        this.updatePopupInfo(getTenantId());
      })
    );

    this.subscription.add(
      this.userCurrentTenantId$.subscribe((tenantId: string) => {
        this.updatePopupInfo(tenantId);
      })
    );
  }

  private updatePopupInfo(tenantId: string): void {
    if (this.userAccess) {
      this.profileSettingsAvailable = this.userAccess.tenantInfos.length > 0;
      this.tenantUserName = this.userAccess.name;
      this.tenantUserEmail = this.userAccess?.email
        .replace(/\./g, '\u200B.')
        .replace(/@/, '\u200B@');
      this.tenantPosition = this.userAccess.isProductOwner
        ? 'productOwner'
        : this.userAccess.tenantInfos.find((tenantInfo) => tenantInfo.id === tenantId)?.position
            ?.name || 'user';
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onToggleDarkMode(event: UIEvent): void {
    this.isDarkMode = !this.isDarkMode;
    this.store.dispatch(new ToggleDarkMode(this.isDarkMode));
    event.stopPropagation();
  }

  onToggleFilterSaving(event: UIEvent, filterSavingToggle: MatSlideToggle): void {
    const currentValue = this.filterSavingEnabled;
    event.stopPropagation();
    if (currentValue) {
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: 'disableFilterSaving',
            message: 'disableFilterSavingMessage',
            confirmButtonText: 'confirm',
            cancelButtonText: 'cancel',
            confirmButton: 'warn-secondary',
          },
        })
        .afterClosed()
        .subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.filterSavingEnabled = false;
            this.tableFilterStorageService.setFilterSavingEnabled(false);
            this.store.dispatch(new ClearAllSavedFilters());
          }
          filterSavingToggle.checked = this.filterSavingEnabled;
        });
    } else {
      this.filterSavingEnabled = true;
      this.tableFilterStorageService.setFilterSavingEnabled(this.filterSavingEnabled);
    }
  }

  onClearAllFilters(): void {
    this.store.dispatch(new ClearAllSavedFilters());
  }

  onProfileSettings(): void {
    this.store.dispatch(new ClearFilterDetails());
    this.router.navigate([`${RoutesEnum.Profile}/view`]);
  }

  changeTenant(): void {
    this.dialog
      .open(TenantSelectorComponent, {
        panelClass: 'mobile-sizing-for-mat-dialogs',
        width: '50rem',
      })
      .afterClosed()
      .subscribe();
  }
}
